import { useLocalStorageAsState } from "@msys/ui";
import React from "react";
import { useUpdateEffect } from "react-use";

export const ALL_KEY = "__all__";

export const defaultAllClosedValue = {};
export const defaultAllOpenedValue = { [ALL_KEY]: true };

export type ExpandedStore = {
  isItemExpanded: (itemId: string, defaultExpanded?: boolean) => boolean;
  setItemExpanded: (itemId: string, expanded: boolean) => void;
  setItemsExpanded: (itemIds: string[], expanded: boolean) => void;
  setExpandedState: (state: { [itemId: string]: boolean }) => void;
  setAllItemsExpanded: (expanded: boolean) => void;
  areAllItemsExpanded: boolean;
  expandedItemIds: string[] | undefined;
};

export type StoreType =
  | "quote-template"
  | "building"
  | "requirement"
  | "quote"
  | "tasks"
  | "invoice"
  | "decisions";

export function useExpandedStoreWithLocalStorage(
  storeType: StoreType,
  docId: string,
  selectedItemId?: string | null
): ExpandedStore {
  const [value, setValue] = useLocalStorageAsState<{ [key: string]: boolean }>(
    `msys-expanded-${storeType}-${docId}`,
    defaultAllClosedValue,
    true
  );

  const store: ExpandedStore = useExpandedStore(value, setValue);

  const ref = React.useRef(store);
  ref.current = store;

  useUpdateEffect(() => {
    if (selectedItemId) {
      if (!ref.current.isItemExpanded(selectedItemId)) {
        ref.current.setItemExpanded(selectedItemId, true);
      }
    }
  }, [selectedItemId]);

  return store;
}

export function useExpandedStoreWithComponentState(): ExpandedStore {
  const [value, setValue] = React.useState<{
    [key: string]: boolean;
  }>({});

  const store = useExpandedStore(value, setValue);

  return store;
}

interface Value {
  [key: string]: boolean;
}
interface SetValue {
  (newValue: Value): void;
}

function useExpandedStore(value: Value, setValue: SetValue) {
  return React.useMemo(
    (): ExpandedStore => ({
      isItemExpanded: (itemId: string) => {
        return value[ALL_KEY] || (value[itemId] ?? false);
      },
      setItemExpanded: (itemId: string, expanded: boolean) => {
        // everything is already expanded
        if (expanded && value[ALL_KEY]) return;
        // already done, nothing to change (unless ALL_KEY needs to be reset)
        if (
          Boolean(value[itemId]) === expanded &&
          !(!expanded && value[ALL_KEY])
        )
          return;
        setValue({
          ...value,
          ...(!expanded && value[ALL_KEY] ? { [ALL_KEY]: false } : undefined),
          [itemId]: expanded,
        });
      },
      setItemsExpanded: (itemIds: string[], expanded: boolean) => {
        // already done, nothing to change
        if (itemIds.every(id => Boolean(value[id]) === expanded)) return;
        setValue({
          ...value,
          ...(!expanded && value[ALL_KEY] ? { [ALL_KEY]: false } : undefined),
          ...Object.fromEntries(itemIds.map(itemId => [itemId, expanded])),
        });
      },
      setExpandedState: (state: { [itemId: string]: boolean }) => {
        setValue({ ...value, ...state });
      },
      areAllItemsExpanded: Boolean(value[ALL_KEY]),
      setAllItemsExpanded: (expanded: boolean) => {
        setValue({ [ALL_KEY]: expanded });
      },
      expandedItemIds: value[ALL_KEY]
        ? undefined
        : Object.entries(value)
            .filter(([key, value]) => value)
            .map(([key, value]) => key),
    }),
    [setValue, value]
  );
}
